import React from 'react';
import styled from 'styled-components';
import deleteOpenIcon from '../../../asset/icon/delete_open.png';
import deleteCloseIcon from '../../../asset/icon/delete_close.png';
import orangeIcon from '../../../asset/icon/orange.png';
import defaultImage from '../../../asset/project/default_1.png';
import { imHere } from '../../../shared/component/Animation';
import { convertS3UrlToCloudFrontUrl } from '../../../shared/aws/awsHelper';
import SelectBox from '../../../shared/component/SelectBox';
import UniversalIconBtn from '../../../shared/component/headless/button/UniversalIconBtn';
import { WorkViewerInfo } from '../../../shared/component/headless/input/InputBody';
import { NormalSmallTextArea } from '../../../shared/component/headless/textarea/TextAreaBody';
import OptimizedMoleculeImg from '../../../shared/component/molecule/OptimizedMoleculeImg';
import MoleculeInputDiv from '../../../shared/component/molecule/MoleculeInputDiv';
import MoleculeTextBlockLink from '../../../shared/component/molecule/MoleculeTextBlockLink';
import ConstrainedRichText from '../../../shared/richText/ConstrainedRichText';
import { getAlertMessage } from '../../../shared/constants/alertMessages';
import {
  TEXTBLOCK_CAPTION_MAX_LENGTH,
  TEXTBLOCK_IMAGE_MAX_COUNT,
  TEXTBLOCK_PARAGRAPH_MAX_LENGTH,
} from '../../../shared/constants/limits';
import { TextBlockData } from '../../../shared/dto/EntityRepository';
import { StyledImgProps } from '../../../shared/dto/StyleCompRepository';
import {
  AlertPosition,
  AlertType,
  DisplayAlignment,
  DisplaySize,
  SelectType,
  TextBlockType,
} from '../../../shared/enum/EnumRepository';
import { useTextBlock } from '../../../shared/hooks/useApi/useTextBlock';
import { useEditMode } from '../../../shared/hooks/useEditMode';
import { useStandardAlertStore } from '../../../shared/store/portal/alertStore';
import { useTextViewStore } from '../../../shared/store/TextViewStore';
import {
  getTextBlockCaptionForTypeChange,
  getTextBlockLinkCaption,
} from '../../../shared/util/textBlockLinkCaption';

interface TextBlockProps {
  data: TextBlockData;
}

const TextBlockFixed: React.FC<TextBlockProps> = ({ data }) => {
  const { isEditMode } = useEditMode();
  const isLargeBody = useTextViewStore((s) => s.isLargeBody);
  const updateActiveTextBlockList = useTextViewStore((s) => s.updateActiveTextBlockList);
  const getActiveTextBlockImageCount = useTextViewStore((s) => s.getActiveTextBlockImageCount);
  const { deleteTextBlock } = useTextBlock();
  const { enqueueAlert } = useStandardAlertStore();

  const handleLinkCaptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    updateActiveTextBlockList(data.id, { caption: e.target.value });
  };

  const handleParagraphChange = (next: string) => {
    updateActiveTextBlockList(data.id, {
      textContent: next,
    });
  };

  const handleTextTypeChange = (value: TextBlockType) => {
    if (value === TextBlockType.IMAGE) {
      const imageCount = getActiveTextBlockImageCount();
      if (imageCount >= TEXTBLOCK_IMAGE_MAX_COUNT) {
        enqueueAlert({
          type: AlertType.ALERT,
          position: AlertPosition.TOP,
          content: getAlertMessage('TEXTBLOCK_IMAGE_MAX_EXCEEDED', {
            maxCount: TEXTBLOCK_IMAGE_MAX_COUNT,
          }),
        });
        return;
      }
    }
    const nextCaption = getTextBlockCaptionForTypeChange(data, value);
    updateActiveTextBlockList(data.id, {
      type: value,
      ...(nextCaption !== undefined ? { caption: nextCaption } : {}),
    });
  };

  const handleImageChange = (originUrl: string, width: number, height: number) => {
    if (data.type !== TextBlockType.IMAGE) return;
    updateActiveTextBlockList(data.id, {
      uploadFile: {
        id: data.uploadFile?.id ?? '',
        allowDownload: false,
        originUrl,
        width,
        height,
      },
      imageChanged: true,
    });
  };

  const handleCaptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateActiveTextBlockList(data.id, { caption: e.target.value });
  };

  const handleLinkUrlChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    updateActiveTextBlockList(data.id, { url: e.target.value });
  };

  const handleDelete = () => {
    enqueueAlert({
      type: AlertType.CONFIRM,
      position: AlertPosition.TOP,
      content: getAlertMessage('CONFIRM_DELETE_TEXT_BLOCK'),
      callBack: () => deleteTextBlock({ textBlockId: data.id }),
    });
  };

  const renderTextBlock = () => {
    switch (data.type) {
      case TextBlockType.PARAGRAPH:
        return (
          <ConstrainedRichText
            value={data.textContent}
            placeholder="Enter description"
            onChange={handleParagraphChange}
            StyledView={Paragraph}
            maxLength={TEXTBLOCK_PARAGRAPH_MAX_LENGTH}
            maxRows={100}
          />
        );
      case TextBlockType.IMAGE: {
        const imageUrl = data.uploadFile?.originUrl
          ? convertS3UrlToCloudFrontUrl(data.uploadFile.originUrl)
          : defaultImage;
        return (
          <TextBlockImageWrapper>
            <ImageWrapper>
              <OptimizedMoleculeImg
                srcUrl={imageUrl}
                alt="Image Block"
                displaySize={DisplaySize.REGULAR}
                displayAlignment={DisplayAlignment.CENTER}
                handleChange={handleImageChange}
                StyledImg={WorkImage as React.ComponentType<StyledImgProps>}
                priority={true}
              />
            </ImageWrapper>
            <MoleculeInputDiv
              value={data.caption ?? ''}
              placeholder="Caption"
              handleChange={handleCaptionChange}
              inputStyle={CaptionInput}
              StyledDiv={Caption}
              maxLength={TEXTBLOCK_CAPTION_MAX_LENGTH}
            />
          </TextBlockImageWrapper>
        );
      }
      case TextBlockType.LINK:
        return (
          <MoleculeTextBlockLink
            label={getTextBlockLinkCaption(data)}
            url={data.url ?? ''}
            handleChangeLabel={handleLinkCaptionChange}
            handleChangeUrl={handleLinkUrlChange}
            StyledLabel={Paragraph}
            StyledLabelTextArea={ParagraphTextArea}
          />
        );
      default:
        return null;
    }
  };

  return (
    <TextBlockWrapper $isLargeBody={isLargeBody}>
      {isEditMode && (
        <TBHeader>
          <SelectBoxContainer>
            <SelectBoxWrapper>
              <SelectBox
                value={data.type}
                selectType={SelectType.TEXT_BLOCK_TYPE}
                handleChange={handleTextTypeChange}
                direction={false}
              />
            </SelectBoxWrapper>
          </SelectBoxContainer>
          {data.hasChanged && (
            <ChangedIconWrap>
              <ChangedIcon src={orangeIcon} alt="Unsaved changes" />
            </ChangedIconWrap>
          )}
        </TBHeader>
      )}
      {renderTextBlock()}
      {isEditMode && (
        <BtnContainer>
          <UniversalIconBtn
            size="fit"
            border={false}
            backgroundImg={deleteCloseIcon}
            backgroundImgHover={deleteOpenIcon}
            onClick={handleDelete}
          />
        </BtnContainer>
      )}
    </TextBlockWrapper>
  );
};

export default TextBlockFixed;

const TextBlockWrapper = styled.div<{ $isLargeBody: boolean }>`
  position: relative;
  width: 100%;
  color: ${({ theme }) => theme.colors.color_Gray_02};
  --text-block-body-font-size: ${({ theme, $isLargeBody }) =>
    $isLargeBody ? theme.fontSize.font_B02 : theme.fontSize.font_B03};
  --rich-text-size-s: ${({ theme, $isLargeBody }) =>
    $isLargeBody ? theme.fontSize.font_B03 : theme.fontSize.font_B04};
  --rich-text-size-l: ${({ theme, $isLargeBody }) =>
    $isLargeBody ? theme.fontSize.font_B01 : theme.fontSize.font_B02};
`;

const TextBlockImageWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const Caption = styled.div`
  width: 100%;
  padding: 4px 0;
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_04};
`;

const CaptionInput = styled(WorkViewerInfo)`
  border-bottom: 1px solid ${({ theme }) => theme.colors.color_Gray_05};
  ${({ theme }) => theme.typography.Body_04};
`;

const Paragraph = styled.div`
  width: 100%;
  color: ${({ theme }) => theme.colors.color_Gray_02};
  text-align: justify;
  overflow-wrap: anywhere;
  word-break: keep-all;
  ${({ theme }) => theme.typography.Body_03_2};
  font-size: var(--text-block-body-font-size);
  line-height: 1;
`;

const ParagraphTextArea = styled(NormalSmallTextArea)`
  text-align: justify;
  line-height: 1.85;
  border-bottom: 1px solid ${({ theme }) => theme.colors.color_Gray_06};
  &::-webkit-scrollbar {
    display: none;
  }
  ${({ theme }) => theme.typography.Body_03_2};
  font-size: var(--text-block-body-font-size);
`;

const TBHeader = styled.div`
  position: absolute;
  top: -22px;
  width: 100%;

  display: flex;
  justify-content: space-between;
`;

const ChangedIconWrap = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  ${imHere}
`;

const ChangedIcon = styled.img`
  width: 12px;
  height: 12px;
  margin: 4px;
`;

export const SelectBoxContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  gap: clamp(12px, 1vw, 14px);
`;

export const SelectBoxWrapper = styled.article`
  width: clamp(70px, 16vw, 100px);
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_04};
`;

const BtnContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.5vw;
`;

const WorkImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;
